import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LanguageFlag from '@/components/LanguageFlag';

type Lang = 'sv' | 'en';

const content = {
  sv: {
    title: 'Radera konto',
    intro:
      'På den här sidan beskriver vi hur du raderar ditt konto i Beredskapshubben och vilken data som tas bort.',
    steps: {
      heading: 'Så raderar du ditt konto',
      items: [
        'Logga in på Beredskapshubben på beredskapshubben.se eller i appen.',
        'Gå till Inställningar (kugghjulet uppe till höger eller i menyn).',
        'Scrolla ner till avsnittet "Konto" och tryck på "Radera konto".',
        'Bekräfta borttagningen i dialogrutan som visas.',
      ],
    },
    email: {
      heading: 'Radering via e-post',
      text: 'Om du inte kan logga in kan du skicka en begäran om kontoborttagning till',
      address: 'support@beredskapshubben.se',
      note: 'Ange den e-postadress som är kopplad till ditt konto.',
    },
    deleted: {
      heading: 'Data som raderas',
      text: 'När du raderar ditt konto tas följande data bort omedelbart:',
      items: [
        'Din användarprofil (namn, e-postadress och inställningar)',
        'Alla dina checklistor och egna checklistmallar',
        'Ditt lager och vattenförsörjningsdata',
        'Dina inköpslistor, anteckningar och recept',
        'Ditt innehåll i Årshjulet',
        'Dina favoritartiklar',
        'Din familj (om du är ägare) eller ditt familjemedlemskap',
        'Ditt gruppmedlemskap och grupprelaterade inbjudningar',
        'Dina inloggningsuppgifter (lösenord/autentiseringsdata)',
      ],
    },
    retained: {
      heading: 'Data som kan behållas',
      items: [
        'Aggregerad och anonymiserad statistik som inte kan kopplas till dig som individ.',
        'Loggar som krävs av lag eller regelverk raderas i enlighet med tillämpliga lagringstider (normalt högst 90 dagar).',
      ],
    },
    privacy: {
      text: 'Läs vår',
      link: 'integritetspolicy',
      suffix: 'för fullständig information om hur vi hanterar personuppgifter.',
    },
  },
  en: {
    title: 'Delete Account',
    intro:
      'This page explains how to delete your Beredskapshubben account and what data will be removed.',
    steps: {
      heading: 'How to delete your account',
      items: [
        'Log in to Beredskapshubben at beredskapshubben.se or in the app.',
        'Go to Settings (gear icon in the top right or in the menu).',
        'Scroll down to the "Account" section and tap "Delete account".',
        'Confirm the deletion in the dialog that appears.',
      ],
    },
    email: {
      heading: 'Deletion by email',
      text: 'If you cannot log in, you may submit a deletion request to',
      address: 'support@beredskapshubben.se',
      note: 'Please include the email address associated with your account.',
    },
    deleted: {
      heading: 'Data that will be deleted',
      text: 'When you delete your account, the following data is removed immediately:',
      items: [
        'Your user profile (name, email address, and settings)',
        'All your checklists and custom checklist templates',
        'Your inventory and water supply data',
        'Your shopping lists, notes, and recipes',
        'Your Year Wheel content',
        'Your favorite articles',
        'Your family (if you are the owner) or your family membership',
        'Your group memberships and group-related invitations',
        'Your login credentials (password/authentication data)',
      ],
    },
    retained: {
      heading: 'Data that may be retained',
      items: [
        'Aggregated and anonymised statistics that cannot be linked to you as an individual.',
        'Logs required by law or regulation are deleted in accordance with applicable retention periods (normally no more than 90 days).',
      ],
    },
    privacy: {
      text: 'Read our',
      link: 'privacy policy',
      suffix: 'for full information on how we handle personal data.',
    },
  },
};

export default function DeleteAccountPage() {
  const [lang, setLang] = useState<Lang>('sv');
  const c = content[lang];

  useEffect(() => {
    document.title =
      lang === 'sv'
        ? 'Radera konto - Beredskapshubben'
        : 'Delete Account - Beredskapshubben';
  }, [lang]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold text-primary hover:opacity-80 transition-opacity">
            Beredskapshubben
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang('sv')}
              className={`px-2 py-1 rounded text-sm transition-colors ${
                lang === 'sv'
                  ? 'bg-primary text-primary-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              aria-label="Svenska"
            >
              <span className="inline-flex items-center gap-1.5">
                <LanguageFlag lang="sv" />
                SV
              </span>
            </button>
            <button
              onClick={() => setLang('en')}
              className={`px-2 py-1 rounded text-sm transition-colors ${
                lang === 'en'
                  ? 'bg-primary text-primary-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              aria-label="English"
            >
              <span className="inline-flex items-center gap-1.5">
                <LanguageFlag lang="en" />
                EN
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-10 space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{c.title}</h1>
          <p className="mt-3 text-muted-foreground">{c.intro}</p>
        </div>

        {/* Steps */}
        <section className="rounded-lg border border-border bg-card p-6 space-y-4">
          <h2 className="text-xl font-semibold">{c.steps.heading}</h2>
          <ol className="space-y-2 list-none">
            {c.steps.items.map((item, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="text-sm leading-relaxed pt-0.5">{item}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* Email deletion */}
        <section className="rounded-lg border border-border bg-card p-6 space-y-2">
          <h2 className="text-xl font-semibold">{c.email.heading}</h2>
          <p className="text-sm text-muted-foreground">
            {c.email.text}{' '}
            <a
              href={`mailto:${c.email.address}`}
              className="text-primary underline underline-offset-2"
            >
              {c.email.address}
            </a>
            .
          </p>
          <p className="text-sm text-muted-foreground">{c.email.note}</p>
        </section>

        {/* Deleted data */}
        <section className="rounded-lg border border-border bg-card p-6 space-y-4">
          <h2 className="text-xl font-semibold">{c.deleted.heading}</h2>
          <p className="text-sm text-muted-foreground">{c.deleted.text}</p>
          <ul className="space-y-1">
            {c.deleted.items.map((item, i) => (
              <li key={i} className="flex gap-2 text-sm">
                <span className="text-primary mt-0.5">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Retained data */}
        <section className="rounded-lg border border-border bg-card p-6 space-y-4">
          <h2 className="text-xl font-semibold">{c.retained.heading}</h2>
          <ul className="space-y-2">
            {c.retained.items.map((item, i) => (
              <li key={i} className="text-sm text-muted-foreground">
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Privacy policy link */}
        <p className="text-sm text-muted-foreground">
          {c.privacy.text}{' '}
          <Link to="/privacy" className="text-primary underline underline-offset-2">
            {c.privacy.link}
          </Link>{' '}
          {c.privacy.suffix}
        </p>
      </main>
    </div>
  );
}
